import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DragDropContext, Draggable } from 'react-beautiful-dnd';
import { StrictModeDroppable } from 'spw-components/global/strict-mode-droppable';
import { getListStyle } from 'spw-components/programming-scenarios/styles';
import { getItemStyle } from 'spw-components/worksheet/styles';
import {
  selectAllChildWorksheetSpaces,
  selectCurrentScenario,
  selectIsRowDisabled,
  selectShowWorksheetUpdateDialog,
  selectWorksheet,
} from 'spw-redux/modules/calculators/selectors';
import { isTotalProgram } from 'spw-components/programming-scenarios/building-hs-sc-scenario/utils';
import ParentSpaceTypeRow from 'spw-programming-projects/building-details/worksheet/parent-space-type-row';
import SpaceTypeRow from 'spw-programming-projects/building-details/worksheet/space-type-row';
import { WorksheetContext } from 'spw-programming-projects/building-details/worksheet/worksheet-context';
import { IChildWorksheetSpaceType } from 'spw-redux/modules/calculators/types';
import { getRowId } from 'spw-programming-projects/building-details/worksheet/utils';
import { reorder } from 'spw-shared/utils';
import { useReorderItems } from 'spw-shared/hooks/use-reorder-items';
import { updateChildSpaceTypePosition } from 'spw-redux/modules/worksheet/actions';
import { RowWrapper } from 'spw-programming-projects/building-details/worksheet/styles';
import { selectCurrentUserIsViewOnly } from 'spw-redux/modules/projects/selectors';
import { useChildRowsBySpaceType } from 'spw-programming-projects/building-details/worksheet/hooks';
import WorksheetRow from '../../../../models/WorksheetRow';

type SpaceTypeRowsProps = {
  row: WorksheetRow;
  totalCategorySeats: number;
  isDraggingOver?: boolean;
  parentRowIndex?: number;
  areAllParentsCollapsed: boolean;
};

const TotalProgramNestedRows = ({
  row,
  shouldShowChildRows,
  childRows,
  totalCategorySeats,
}: {
  row: WorksheetRow;
  shouldShowChildRows: boolean;
  childRows: WorksheetRow[];
  totalCategorySeats: number;
}) => {
  const worksheet = useSelector(selectWorksheet);
  const departmentRows = worksheet.breakDownRows[row.uniqueKey] || [];

  if (shouldShowChildRows) {
    return (
      <>
        {childRows.map((childRow: WorksheetRow) => {
          return (
            <SpaceTypeRows
              key={childRow.id}
              row={childRow}
              totalCategorySeats={totalCategorySeats}
              areAllParentsCollapsed={false}
            />
          );
        })}
        {departmentRows.map((childRow: WorksheetRow) => {
          return (
            <SpaceTypeRow
              key={childRow.id}
              row={childRow}
              totalCategorySeats={totalCategorySeats}
              isPerDepartmentRow
            />
          );
        })}
      </>
    );
  }
  return null;
};

const SpaceTypeRows: React.FC<SpaceTypeRowsProps> = ({
  row,
  totalCategorySeats,
  isDraggingOver,
  parentRowIndex,
  areAllParentsCollapsed,
}) => {
  const scenario = useSelector(selectCurrentScenario);
  const worksheet = useSelector(selectWorksheet);
  const currentUserIsViewOnly = useSelector(selectCurrentUserIsViewOnly);
  const isShowingWorksheetUpdateDialog = useSelector(
    selectShowWorksheetUpdateDialog
  );
  const isDisabled = useSelector(selectIsRowDisabled);
  const dispatch = useDispatch();
  const totalProgram = isTotalProgram(scenario);
  const isParentRow = !row.child && scenario.isDetailed;

  const {
    showDecimals,
    clientFacingNamesAreVisible,
    userIsEditingASpaceName,
    activeRowIdentifier,
    renameSpaceType,
    collapsed,
    setCollapsedRow,
    newRowRef,
    newRowId,
  } = useContext(WorksheetContext);

  const shouldShowChildRows: boolean = useMemo(() => {
    if (totalProgram) {
      return !collapsed[getRowId(row)];
    }
    return (
      (scenario.isDetailed || row.isStorageSupportSpace) &&
      !collapsed[getRowId(row)] &&
      !isDraggingOver
    );
  }, [scenario.isDetailed, totalProgram, collapsed, row, isDraggingOver]);

  const childWorksheetSpaceTypes: IChildWorksheetSpaceType[] = useSelector(
    selectAllChildWorksheetSpaces
  );

  const childRowsBySpaceType = useChildRowsBySpaceType();

  const childRows = useMemo(() => {
    return childRowsBySpaceType[row.projectSpaceType.spaceType.id] || [];
  }, [childRowsBySpaceType, row.projectSpaceType.spaceType.id]);

  const [nestedRows, setNestedRows] = useState([]);
  const departmentRows = worksheet.breakDownRows[row.uniqueKey] || [];
  const hasChildDepartmentRows = departmentRows.length > 0;

  useEffect(() => {
    setNestedRows(childRows);
  }, [childRows]);

  const setCollapsed = useCallback(() => {
    setCollapsedRow(row, hasChildDepartmentRows);
  }, [setCollapsedRow, row, hasChildDepartmentRows]);

  const reorderNestedRows = useCallback(
    (sourceIndex: number, destinationIndex: number) => {
      const selectedChildRow = nestedRows[sourceIndex];
      const destinationChildRow = nestedRows[destinationIndex];
      const selectedChildSpaceTypeId = selectedChildRow.spaceType.id;
      const destinationChildSpaceTypeId = destinationChildRow.spaceType.id;

      const selectedChildSpaceTypeIndex = childWorksheetSpaceTypes.findIndex(
        (cwst: IChildWorksheetSpaceType) => cwst.id === selectedChildSpaceTypeId
      );
      const destinationChildSpaceTypeIndex = childWorksheetSpaceTypes.findIndex(
        (cwst: IChildWorksheetSpaceType) =>
          cwst.id === destinationChildSpaceTypeId
      );

      if (destinationChildSpaceTypeIndex === 0) {
        const nextChildSpaceType =
          childWorksheetSpaceTypes[destinationChildSpaceTypeIndex];

        dispatch(
          updateChildSpaceTypePosition(
            selectedChildSpaceTypeId,
            null,
            nextChildSpaceType.id
          )
        );
      } else if (
        destinationChildSpaceTypeIndex ===
        childWorksheetSpaceTypes.length - 1
      ) {
        const previousChildSpaceType =
          childWorksheetSpaceTypes[destinationChildSpaceTypeIndex];

        dispatch(
          updateChildSpaceTypePosition(
            selectedChildSpaceTypeId,
            previousChildSpaceType.id,
            null
          )
        );
      } else {
        let previousChildSpaceType;
        let nextChildSpaceType;
        if (destinationChildSpaceTypeIndex > selectedChildSpaceTypeIndex) {
          previousChildSpaceType =
            childWorksheetSpaceTypes[destinationChildSpaceTypeIndex];
          nextChildSpaceType =
            childWorksheetSpaceTypes[destinationChildSpaceTypeIndex + 1];
        } else {
          previousChildSpaceType =
            childWorksheetSpaceTypes[destinationChildSpaceTypeIndex - 1];
          nextChildSpaceType =
            childWorksheetSpaceTypes[destinationChildSpaceTypeIndex];
        }

        dispatch(
          updateChildSpaceTypePosition(
            selectedChildSpaceTypeId,
            previousChildSpaceType.id,
            nextChildSpaceType.id
          )
        );
      }

      const orderedNestedRows = reorder(
        nestedRows,
        sourceIndex,
        destinationIndex
      );
      setNestedRows(orderedNestedRows);
    },
    [nestedRows, childWorksheetSpaceTypes, dispatch]
  );
  const isNotDraggable =
    isTotalProgram(scenario) ||
    currentUserIsViewOnly ||
    isShowingWorksheetUpdateDialog ||
    userIsEditingASpaceName;

  const areParentsNotDraggable =
    isTotalProgram(scenario) ||
    !areAllParentsCollapsed ||
    currentUserIsViewOnly ||
    isShowingWorksheetUpdateDialog ||
    userIsEditingASpaceName;

  const { onDragEnd } = useReorderItems(reorderNestedRows);

  if (isParentRow || hasChildDepartmentRows) {
    const parentRow = (
      <ParentSpaceTypeRow
        belongsToTotalProgram={isTotalProgram(scenario)}
        showDecimals={showDecimals}
        disabled={isDisabled}
        active={activeRowIdentifier === `${row.spaceType.id}-${!!row.child}`}
        name={row.spaceType?.name}
        row={row}
        spaceTypeTotals={worksheet.spaceTypesTotals[row.uniqueKey]}
        totalCategorySeats={totalCategorySeats}
        collapsed={collapsed[getRowId(row)] || isDraggingOver}
        setCollapsed={setCollapsed}
        renameSpaceType={renameSpaceType}
        shouldDisplayRowValues={hasChildDepartmentRows}
        isPerDepartmentParentRow={hasChildDepartmentRows}
        clientFacingNamesAreVisible={clientFacingNamesAreVisible}
        isDraggable={!isNotDraggable && !areParentsNotDraggable}
      />
    );

    return (
      <RowWrapper
        ref={
          `${row.projectSpaceType.id}-${!!row.child}` === newRowId
            ? newRowRef
            : null
        }
      >
        {totalProgram ? (
          parentRow
        ) : (
          <Draggable
            key={row.id}
            draggableId={`${row.projectSpaceType.id}`}
            index={parentRowIndex}
            isDragDisabled={areParentsNotDraggable}
          >
            {(provided, snapshot) => (
              <div
                ref={provided.innerRef}
                {...provided.draggableProps}
                {...provided.dragHandleProps}
                style={{
                  ...getItemStyle(
                    snapshot.isDragging,
                    provided.draggableProps.style
                  ),
                  outline: 'none',
                  outlineColor: 'transparent',
                }}
              >
                {parentRow}
              </div>
            )}
          </Draggable>
        )}

        {totalProgram ? (
          <TotalProgramNestedRows
            shouldShowChildRows={shouldShowChildRows}
            row={row}
            childRows={childRows}
            totalCategorySeats={totalCategorySeats}
          />
        ) : (
          <DragDropContext onDragEnd={onDragEnd}>
            <StrictModeDroppable droppableId={`${row.projectSpaceType.id}`}>
              {(droppableProvided, droppableSnapshot) => (
                <div
                  {...droppableProvided.droppableProps}
                  ref={droppableProvided.innerRef}
                  style={getListStyle(droppableSnapshot.isDraggingOver)}
                >
                  {shouldShowChildRows &&
                    nestedRows.map((childRow: WorksheetRow, index: number) => {
                      return (
                        <RowWrapper
                          key={childRow.id}
                          ref={
                            `${
                              childRow.projectSpaceType.id
                            }-${!!childRow.child}` === newRowId
                              ? newRowRef
                              : null
                          }
                        >
                          <Draggable
                            key={childRow.id}
                            draggableId={`${childRow.spaceId}`}
                            index={index}
                            isDragDisabled={isNotDraggable}
                          >
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                style={{
                                  ...getItemStyle(
                                    snapshot.isDragging,
                                    provided.draggableProps.style
                                  ),
                                  outline: 'none',
                                  outlineColor: 'transparent',
                                }}
                              >
                                <SpaceTypeRow
                                  key={childRow.id}
                                  row={childRow}
                                  totalCategorySeats={totalCategorySeats}
                                  isDraggable={!isNotDraggable}
                                />
                              </div>
                            )}
                          </Draggable>
                        </RowWrapper>
                      );
                    })}
                  {droppableProvided.placeholder}
                </div>
              )}
            </StrictModeDroppable>
          </DragDropContext>
        )}
      </RowWrapper>
    );
  }

  return (
    <Draggable
      key={row.id}
      draggableId={`${row.projectSpaceType.id}`}
      index={parentRowIndex}
      isDragDisabled={isNotDraggable}
    >
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          style={{
            ...getItemStyle(snapshot.isDragging, provided.draggableProps.style),
            outline: 'none',
            outlineColor: 'transparent',
          }}
        >
          <SpaceTypeRow
            key={row.id}
            row={row}
            totalCategorySeats={totalCategorySeats}
            isDraggable
          />
        </div>
      )}
    </Draggable>
  );
};

export default SpaceTypeRows;
